import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, ChevronRight, Rocket, Sparkles, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { fetchMembers } from "../../features/members/slice-extensions/members-slice-extension";
import { fetchContracts } from "../../features/contracts/slice-extensions/contracts-slice-extension";
import { fetchSessionsPaginated } from "../../features/sessions/slice-extensions/sessions-slice-extension";
import {
  selectSampleDataFetchStatus,
  selectSampleDataStatus,
} from "../../features/sample-data/sample-data-slice";
import { fetchSampleDataStatus } from "../../features/sample-data/slice-extensions/sample-data-slice-extension";
import SampleDataTour from "../../features/sample-data/components/SampleDataTour";
import LoadSampleDataOffer from "../../features/sample-data/components/LoadSampleDataOffer";
import { LoadingStatus } from "../../features/sessions/models/LoadingStatus";
import { ROUTES } from "../../routes";
import { selectOrganization } from "../../features/organization/organization-slice";
import { OrganizationKind } from "../../features/organization/enums/OrganizationKind";
import FahrschuleChecklist from "./FahrschuleChecklist";
import OnboardingStepList, { OnboardingStep } from "./OnboardingStepList";
import OnboardingWelcomeCard, { WELCOME_DISMISSED_KEY } from "./OnboardingWelcomeCard";
import { tutorialUrl } from "./tutorialUrl";

const DISMISSED_KEY = "tp-onboarding-dismissed";
const STARTED_KEY = "tp-onboarding-started";

interface SetupCounts {
  members: number;
  contracts: number;
  sessions: number;
}

/**
 * First-run guided path for an empty account: add a member → their contract →
 * their weekly session. Steps complete as real data appears; once all three are
 * done the card hands over to attendance + month-end and can be dismissed.
 * Accounts that were never empty (no "started" marker) never see the card.
 * A fresh organization (no members, no sessions) also gets the welcome card above it.
 */

const OnboardingChecklist: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();

  const [counts, setCounts] = useState<SetupCounts | null>(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISSED_KEY) != null);
  const [welcomeDismissed, setWelcomeDismissed] = useState(
    () => localStorage.getItem(WELCOME_DISMISSED_KEY) != null
  );
  const sampleStatus = useAppSelector(selectSampleDataStatus);
  const sampleFetchStatus = useAppSelector(selectSampleDataFetchStatus);
  const organization = useAppSelector(selectOrganization);

  useEffect(() => {
    if (sampleStatus === null && sampleFetchStatus === LoadingStatus.Idle) {
      dispatch(fetchSampleDataStatus());
    }
  }, [sampleStatus, sampleFetchStatus, dispatch]);

  useEffect(() => {
    let active = true;
    Promise.all([
      dispatch(fetchMembers()).unwrap(),
      dispatch(fetchContracts({ page: 1, pageSize: 1 })).unwrap(),
      dispatch(fetchSessionsPaginated({ page: 1, pageSize: 1 })).unwrap(),
    ])
      .then(([members, contracts, sessions]) => {
        if (!active) return;
        setCounts({
          members: members.length,
          contracts: contracts.totalCount,
          sessions: sessions.totalCount,
        });
      })
      .catch(() => {
        // A failed probe just means no checklist this visit — the home page works without it.
      });
    return () => {
      active = false;
    };
  }, [dispatch]);

  // With sample data loaded, the checklist hands over to the guided tour —
  // the "add your first member" path makes no sense next to a full account.
  if (sampleStatus?.active) return <SampleDataTour status={sampleStatus} />;

  // A Fahrschule set up by its preset has its sessions already; its checklist is prices, FL-Nr., address.
  if (organization?.kind === OrganizationKind.Fahrschule) return <FahrschuleChecklist />;

  if (!counts) return null;

  // The welcome card leads only while nothing exists yet and has its own dismissal.
  // While it shows it holds the screen's one primary action, so the checklist goes quiet.
  const fresh = counts.members === 0 && counts.sessions === 0;
  const showWelcome = fresh && !welcomeDismissed;
  const dismissWelcome = (): void => {
    localStorage.setItem(WELCOME_DISMISSED_KEY, "1");
    setWelcomeDismissed(true);
  };
  const welcome = showWelcome ? <OnboardingWelcomeCard onDismiss={dismissWelcome} /> : null;

  if (dismissed) return welcome;

  const steps: OnboardingStep[] = [
    {
      key: "member",
      done: counts.members > 0,
      label: t("onboarding.stepMember"),
      hint: t("onboarding.stepMemberHint"),
      action: t("onboarding.stepMemberAction"),
      to: ROUTES.CREATE_MEMBER,
    },
    {
      key: "contract",
      done: counts.contracts > 0,
      label: t("onboarding.stepContract"),
      hint: t("onboarding.stepContractHint"),
      action: t("onboarding.stepContractAction"),
      to: `${ROUTES.BILLING}?tab=contracts`,
    },
    {
      key: "session",
      done: counts.sessions > 0,
      label: t("onboarding.stepSession"),
      hint: t("onboarding.stepSessionHint"),
      action: t("onboarding.stepSessionAction"),
      to: ROUTES.CREATE_SESSION,
    },
  ];

  const allDone = steps.every((s) => s.done);

  if (allDone && localStorage.getItem(STARTED_KEY) == null) return null;
  if (!allDone) localStorage.setItem(STARTED_KEY, "1");

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, "1");
    setDismissed(true);
  };

  return (
    <>
      {welcome}
      <div className="with-border rounded-2xl p-4 lg:p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <Rocket size={20} className="text-primary-40 dark:text-primary-90" />
            <h3 className="text-grey-10 dark:text-grey-90">
              {allDone ? t("onboarding.doneTitle") : t("onboarding.title")}
            </h3>
          </div>
          {allDone && (
            <button
              type="button"
              onClick={dismiss}
              aria-label={t("onboarding.dismiss")}
              className="rounded-md p-1 text-grey-30 hover:bg-grey-95 dark:text-grey-70 dark:hover:bg-primary-20"
            >
              <X size={18} />
            </button>
          )}
        </div>

        {allDone ? (
          <div className="mt-3">
            <p className="text-sm text-grey-30 dark:text-grey-70">{t("onboarding.doneText")}</p>
            <div className="mt-3 flex flex-wrap gap-3">
              <Link to={ROUTES.ATTENDANCE} className="btn btn-primary">
                <span>{t("onboarding.doneAttendance")}</span>
                <ChevronRight size={16} />
              </Link>
              <Link to={`${ROUTES.BILLING}?tab=month-end`} className="btn btn-secondary">
                <span>{t("onboarding.doneMonthEnd")}</span>
                <ChevronRight size={16} />
              </Link>
            </div>
          </div>
        ) : (
          <>
            <p className="mt-1 text-sm text-grey-30 dark:text-grey-70">{t("onboarding.subtitle")}</p>
            <OnboardingStepList steps={steps} quiet={showWelcome} />
            {!showWelcome && (
              <>
                <Link
                  to={ROUTES.ASSISTANT_IMPORT}
                  className="mt-3 flex items-center gap-2 text-sm font-medium text-primary-40 hover:underline dark:text-primary-90"
                >
                  <Sparkles size={16} className="shrink-0" />
                  <span>{t("onboarding.aiImport")}</span>
                </Link>
                <a
                  href={tutorialUrl(i18n.language)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 flex items-center gap-2 text-sm font-medium text-primary-40 hover:underline dark:text-primary-90"
                >
                  <BookOpen size={16} className="shrink-0" />
                  <span>{t("onboarding.tutorialLink")}</span>
                </a>
              </>
            )}
            {fresh && <LoadSampleDataOffer className="mt-4" quiet={showWelcome} />}
          </>
        )}
      </div>
    </>
  );
};

export default OnboardingChecklist;
